import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { get } from "lodash";
import { Stack, Avatar, Box, IconButton, Popover, Typography } from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';
import LanguageIcon from '@mui/icons-material/Language';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { FaFacebookMessenger } from "react-icons/fa";
import { FaViber } from "react-icons/fa";
import { WEB_WIDGET_BUTTON_LOGO_SVG, LAUNCHER_BUBBLE_SIZES } from "../constants";
import useSettingsSelector from "../store/selectors/settings";
import { WidgetContext } from "../store/widgetContext";
import { resizeFrame, uiState, welcomePopover, updateButtonPosition, updateVoiceDimensions } from "../routes/host-bridge";
import VoiceWidget from "./VoiceWidget/VoiceWidget";
import MessageIcon from "./Icons/MessageIcon";
import ChatCircleIcon from "./Icons/ChatCircleIcon";
import QuestionCircleIcon from "./Icons/QuestionCircleIcon";
import SettingsCircleIcon from "./Icons/SettingsCircleIcon";
import HeadphonesIcon from "./Icons/HeadphonesIcon";
import UserVoiceIcon from "./Icons/UserVoiceIcon";
import IntelSwiftDefaultLogo from "./Icons/IntelSwiftDefaultLogo";

const DEFAULT_ICONS = {
	'intelswift-logo': <IntelSwiftDefaultLogo sx={{ fontSize: '1.35em' }} />,
	'document': <MessageIcon sx={{ fontSize: '1.35em' }} />,
	'chat-bubble': <ChatCircleIcon sx={{ fontSize: '1.35em' }} />,
	'question-answer': <QuestionCircleIcon sx={{ fontSize: '1.35em' }} />,
	'settings': <SettingsCircleIcon sx={{ fontSize: '1.35em' }} />,
	'headphones': <HeadphonesIcon sx={{ fontSize: '1.35em' }} />,
	'person': <UserVoiceIcon sx={{ fontSize: '1.35em' }} />,
};

const Launcher = (props) => {
	const context = useContext(WidgetContext);
	const { collapsed, assistant, widget, setCollapsed, hostName, pathName } = context;
	const { tenantId, botId, uuid, assistantId } = props;

	const [channels, setChannels] = useState([]);
	const [openChannels, setOpenChannels] = useState(false);
	const { WEB_WIDGET_BUTTON } = useSettingsSelector();

	const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
	const buttonRef = useRef(null);
	const popoverActionRef = useRef(null);
	const welcomeTimeoutRef = useRef(null);
	const autoCloseTimeoutRef = useRef(null);
	const hasInteractedRef = useRef(false);

	const launcherConfig = get(widget, "chat.launcher", {});
	const BTN_COLOR = get(launcherConfig, "buttonColor", "#6257ff");
	const ICON_COLOR = get(launcherConfig, "iconColor", "#ffffff");
	const BUBBLE_ICON = get(launcherConfig, "bubbleIcon", null);
	const BUBBLE_ICON_TYPE = get(launcherConfig, "bubbleIconType", "default");
	const BUBBLE_SIZE = LAUNCHER_BUBBLE_SIZES[get(launcherConfig, "bubbleSize", "medium")];
	const BOTTOM_POSITION = get(launcherConfig, "bottom", 16);
	const RIGHT_POSITION = get(launcherConfig, "right", 16);
	const WELCOME_MESSAGE = get(launcherConfig, "welcomeMessage", "");
	const POPOVER_DELAY = get(launcherConfig, "popoverDelay", 0);
	const BUTTON_ANIMATION_DELAY = 1300;

	const isVoiceAvailable = assistant?.config?.general?.modes?.includes("voice") && !!assistant?.config?.voiceConfig?.voiceId;
	const isChatAvailable = assistant?.config?.general?.modes?.includes("chat");

	const toggleChannels = () => {
		const spacing = 16;

		if (!openChannels) {
			if (collapsed) {
				uiState("channels");
			}
			const totalButtons = channels.length + 2;
			const totalSpacing = (totalButtons - 1) * spacing;
			const height = totalButtons * BUBBLE_SIZE + totalSpacing;
			resizeFrame({ width: `${BUBBLE_SIZE}px`, height: `${height}px` });
		} else {
			if (collapsed) {
				uiState("launcher");
			}
			resizeFrame({ width: `${BUBBLE_SIZE}px`, height: `${BUBBLE_SIZE}px` });
		}
		setOpenChannels(!openChannels);
	};

	const toggleWebWidget = () => setCollapsed(!collapsed);
	const redirectToMessanger = (channel) => window.open(`${channel.prefix}${channel.name}`, "_blank");

	const handleChannelClick = (origin, channel) => {
		switch (origin) {
		case 'Web':
			setCollapsed(false);
			toggleChannels();
			break;
		case 'Telegram':
			toggleChannels();
			redirectToMessanger(channel);
			break;
		case 'Whatsapp':
			toggleChannels();
			redirectToMessanger(channel);
			break;
		case 'Viber':
			toggleChannels();
			redirectToMessanger(channel);
			break;
		case 'Messenger':
			toggleChannels();
			redirectToMessanger(channel);
			break;
		default:
			console.warn(`Unrecognised channel origin ${origin}.`);
		}

	};

	const getOriginSpecs = (origin) => {
		let avatar = "";
		let color = "";

		switch (origin) {
		case 'Web':
			avatar = <LanguageIcon sx={{ fontSize: '1.35em' }} />;
			color = "#6257FF";
			break;
		case 'Telegram':
			avatar = <TelegramIcon sx={{ fontSize: '1.35em' }} />;
			color = "#0088cc";
			break;
		case 'Whatsapp':
			avatar = <WhatsAppIcon sx={{ fontSize: '1.35em' }} />;
			color = "#25d366";
			break;
		case 'Messenger':
			avatar = <FaFacebookMessenger sx={{ fontSize: '1.35em' }} />;
			color = "#0084FF";
			break;
		case 'Viber':
			avatar = <FaViber sx={{ fontSize: '1.35em' }} />;
			color = "#665CAC";
			break;
		default:
			console.warn(`Unrecognized origin ${origin} for contact avatar`);
			avatar = null;
			color = null;
		}

		return { avatar, color };
	};
	const Channel = ({ channel }) => {
		if (channel) {
			const { color, avatar } = getOriginSpecs(channel.origin);
			return (
				<Avatar
					sx={{
						backgroundColor: color,
						width: BUBBLE_SIZE,
						height: BUBBLE_SIZE,
						objectFit: 'cover',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						fontSize: BUBBLE_SIZE / 2.5,
						cursor: "pointer"
					}}
					onClick={() => { handleChannelClick(channel.origin, channel); }}
				>
					{avatar}
				</Avatar>
			);
		} else {
			return <></>;
		}
	};

	const clearWelcomeTimeouts = () => {
		if (welcomeTimeoutRef.current) {
			clearTimeout(welcomeTimeoutRef.current);
			welcomeTimeoutRef.current = null;
		}
		if (autoCloseTimeoutRef.current) {
			clearTimeout(autoCloseTimeoutRef.current);
			autoCloseTimeoutRef.current = null;
		}
	};

	const handlePopoverClose = () => {
		setShowWelcomeMessage(false);
		clearWelcomeTimeouts();
		setTimeout(() => welcomePopover(false, BUBBLE_SIZE), 200);
	};

	useEffect(() => {
		clearWelcomeTimeouts();

		if (WELCOME_MESSAGE && collapsed && !openChannels && widget && !hasInteractedRef.current) {
			welcomeTimeoutRef.current = setTimeout(() => {
				if (hasInteractedRef.current || !collapsed || openChannels || !buttonRef.current) return;
				
				welcomePopover(true, BUBBLE_SIZE);
				
				requestAnimationFrame(() => {
					setShowWelcomeMessage(true);
					autoCloseTimeoutRef.current = setTimeout(() => handlePopoverClose(), 5000);
				});
			}, POPOVER_DELAY + BUTTON_ANIMATION_DELAY);
		}

		return () => clearWelcomeTimeouts();
	}, [widget, collapsed, openChannels, WELCOME_MESSAGE, POPOVER_DELAY, BUBBLE_SIZE]);

	useEffect(() => {
		if (!collapsed || openChannels) {
			hasInteractedRef.current = true;
			showWelcomeMessage && handlePopoverClose();
		}
	}, [collapsed, openChannels]);

	useEffect(() => {
		if (widget && Array.isArray(widget.channels)) {
			setChannels(widget.channels);
		}
	}, [widget]);

	useEffect(() => {
		if (!showWelcomeMessage) return;
		
		const timeout = setTimeout(() => popoverActionRef.current?.updatePosition?.(), 300);
		
		return () => clearTimeout(timeout);
	}, [showWelcomeMessage, BOTTOM_POSITION, RIGHT_POSITION, BUBBLE_SIZE]);

	useEffect(() => {
		updateButtonPosition({ bottom: BOTTOM_POSITION, right: RIGHT_POSITION, size: BUBBLE_SIZE });
	}, [BOTTOM_POSITION, RIGHT_POSITION, BUBBLE_SIZE]);

	useEffect(() => {
		if (isVoiceAvailable && !isChatAvailable) {
			updateVoiceDimensions();
			return;
		}

		if (collapsed) {
			if (openChannels || showWelcomeMessage) return;

			uiState("launcher");

			setTimeout(() => {
				resizeFrame(
					{ width: `${BUBBLE_SIZE}px`, height: `${BUBBLE_SIZE}px` },
					{ bottom: BOTTOM_POSITION, right: RIGHT_POSITION, reset: true }
				);
			}, 200);

		} else {
			uiState("widget");

			const widgetWidth = get(widget, "chat.dimensions.width", 370);
			const widgetHeight = get(widget, "chat.dimensions.height", 604);
			const containerHeight = widgetHeight + BUBBLE_SIZE + 16;
			const containerWidth = Math.max(widgetWidth, BUBBLE_SIZE);
			resizeFrame(
				{ width: `${containerWidth}px`, height: `${containerHeight}px` },
				{ bottom: BOTTOM_POSITION, right: RIGHT_POSITION }
			);
		}
	}, [collapsed, widget, BOTTOM_POSITION, RIGHT_POSITION, BUBBLE_SIZE, isVoiceAvailable, isChatAvailable, showWelcomeMessage, openChannels]);

	const buttonIcon = useMemo(() => {
		if (!collapsed || openChannels) {
			return <CloseIcon sx={{ fontSize: '1.35em', color: ICON_COLOR || 'white' }} />;
		}

		if (BUBBLE_ICON) {
			if (BUBBLE_ICON_TYPE === "custom") {
				return (
					<img
						src={BUBBLE_ICON}
						alt="Custom icon"
						style={{ width: '70%', height: '70%', objectFit: 'contain', padding: '15%' }}
					/>
				);
			} else if (BUBBLE_ICON_TYPE === "default" && DEFAULT_ICONS[BUBBLE_ICON]) {
				return React.cloneElement(DEFAULT_ICONS[BUBBLE_ICON], { sx: { fontSize: '1.35em', color: ICON_COLOR || 'white' } });
			}
		}

		return WEB_WIDGET_BUTTON_LOGO_SVG;
	}, [collapsed, openChannels, BUBBLE_ICON, BUBBLE_ICON_TYPE, ICON_COLOR]);

	const chatWidget = (
		<>
			<IconButton
				ref={buttonRef}
				className={!collapsed ? "launcher-popup-opened" : "launcher-popup-closed"}
				sx={{
					width: `${BUBBLE_SIZE}px`,
					height: `${BUBBLE_SIZE}px`,
					backgroundColor: BTN_COLOR,
					color: ICON_COLOR || WEB_WIDGET_BUTTON.FONT_COLOR,
					'&:hover': {
						backgroundColor: BTN_COLOR,
						color: ICON_COLOR || WEB_WIDGET_BUTTON.FONT_COLOR,
					},
					animation: !collapsed ? 'rotate 0.3s ease-out forwards' : 'none',
					'@keyframes rotate': {
						'0%': { transform: 'rotate(0deg)' },
						'100%': { transform: 'rotate(90deg)' },
					}
				}}
				onClick={Array.isArray(channels) && channels.length > 0 ? () => {
					toggleChannels();
					if (!openChannels && !collapsed) {
						toggleWebWidget();
						setOpenChannels(false);
					}
				} : toggleWebWidget}
			>
				{buttonIcon}
			</IconButton>
			<Popover
				open={showWelcomeMessage && !!WELCOME_MESSAGE}
				anchorEl={buttonRef.current}
				action={popoverActionRef}
				onClose={handlePopoverClose}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				TransitionProps={{
					timeout: 200,
					onEntered: () => popoverActionRef.current?.updatePosition?.(),
				}}
				disableScrollLock={true}
				marginThreshold={16}
				sx={{
					'& .MuiPaper-root': {
						maxWidth: '280px',
						padding: '12px 16px',
						borderRadius: '12px',
						boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
						marginTop: '-15px',
						transition: 'opacity 200ms ease-out, transform 200ms ease-out',
					}
				}}
			>
				<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
					{WELCOME_MESSAGE}
				</Typography>
			</Popover>
		</>
	);

	return (
		<Box sx={{
			position: 'fixed',
			bottom: '0px',
			right: '0px',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-end',
			height: "auto"
		}}>
			<Stack direction="column" spacing={2}>
				{openChannels && channels.length > 0 && (
					<>
						{Array.isArray(channels) && channels.length > 0 && channels.map((channel, idx) => {
							return (
								<Box key={`channel-${idx}`}>
									<Channel channel={channel} />
								</Box>
							);
						})}
						<Box>
							<Channel channel={{ origin: "Web" }} />
						</Box>
					</>
				)}
				{isVoiceAvailable && !isChatAvailable ?
					<VoiceWidget
						assistantId={assistantId}
						tenantId={tenantId}
						uuid={uuid}
						botId={botId}
						assistant={assistant}
						originHost={hostName}
						originPath={pathName}
					/>
					: (isChatAvailable && chatWidget)
				}
			</Stack>
		</Box>
	);
};

export default Launcher;